<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="classificationForm">
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('area-of-activity') }}</label>
          <p class="label-info">
            A named group of values defining the activity of the Hosting Institution. In case the
            Hosting Institution is offering commercial services the value "Professional Services
            (Commercial)" shall be selected
          </p>
          <v-autocomplete
            v-model="hosting.areasOfActivities"
            :items="areasOfActivities"
            :placeholder="'Area of Activity'"
            :item-value="'id'"
            :item-text="'name'"
            class="required"
            :rules="requiredRule()"
            return-object
            outlined
            clearable
            multiple
          >
          </v-autocomplete>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info fixed-label-height">
                      Keywords associated to the Hosting Institution to simplify search by relevant
                      keywords.
                    </p>
                    <v-combobox
                      v-model="optionalHosting.tags"
                      clearable
                      :placeholder="'Select Tags'"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Type and press <span class="text-bold">enter</span> to create a new
                              tag
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('ecosystem-stakeholder-profile') }}</label>
                    <p class="label-info fixed-label-height">
                      Select from Drop Down list
                    </p>
                    <v-autocomplete
                      v-model="optionalHosting.ecosystem"
                      :items="ecosystem"
                      :placeholder="'Ecosystem Stakeholder Profile'"
                      :item-value="'id'"
                      :item-text="'name'"
                      class="required"
                      :rules="requiredRule()"
                      return-object
                      outlined
                      clearable
                      multiple
                    >
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateHostingClassificationInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import base from '@/services/base-service.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'AddHostingClassificationInfo',
  components: { AlertUnsavedChanges },
  mixins: [util, base],
  data: () => ({
    hosting: {
      areasOfActivities: [],
    },
    optionalHosting: {
      tags: [],
      ecosystem: [],
    },
    ecosystem: [],
    initialEcosystem: [],
    initialAreasOfActivities: [],
    initialTags: [],
    dataLoaded: false,
    unsavedChanges: false,
    areasOfActivities: [],
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    'hosting.areasOfActivities': {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialAreasOfActivities)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalHosting.tags': {
      deep: true,
      handler(newTag) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newTag) !== JSON.stringify(this.initialTags)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalHosting.ecosystem': {
      deep: true,
      handler(newTag) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newTag) !== JSON.stringify(this.initialEcosystem)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
  },
  mounted() {
    this.getAreasOfActivities().then(res => {
      this.areasOfActivities = res.data.content;
    });

    this.getEcosystemStakeholderProfile().then(res => {
      this.ecosystem = res.data.content;
    });

    if (this.edit) {
      this.getHosting();
    }
  },
  methods: {
    back() {
      this.$emit('tab', 1);
    },
    next() {
      if (this.hosting.areasOfActivities.length === 0) {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Area of Activity Required'),
          color: 'error',
        });
        return;
      }

      const areasOfActivities = this.hosting.areasOfActivities.map(activity => ({
        id: activity.id,
        name: activity.name,
      }));
      this.$emit('tab', 3);
      this.$emit('hostingClassificationInfo', {
        areasOfActivities,
      });
    },
    getHosting() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/hosting-institutions/' + id)
        .then(res => {
          console.log('Get Hosting Institution Classification Info', res.data);

          if (res.data.areasOfActivities) {
            this.hosting.areasOfActivities = res.data.areasOfActivities.map(activity => ({
              id: activity.id,
              name: activity.name,
            }));

            this.initialAreasOfActivities = JSON.parse(
              JSON.stringify(this.hosting.areasOfActivities),
            );
          }

          if (res.data.ecosystemStakeholderProfiles) {
            this.optionalHosting.ecosystem = res.data.ecosystemStakeholderProfiles.map(
              ecosystem => ({
                id: ecosystem.id,
                name: ecosystem.name,
              }),
            );

            this.initialEcosystem = JSON.parse(JSON.stringify(this.optionalHosting.ecosystem));
          }

          if (res.data.tags) {
            this.optionalHosting.tags = res.data.tags;

            this.initialTags = JSON.parse(JSON.stringify(this.optionalHosting.tags));
          }

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    removeChip(itemText) {
      this.optionalHosting.tags.forEach(obj => {
        if (obj === itemText) {
          this.optionalHosting.tags.splice(this.optionalHosting.tags.indexOf(obj), 1);
        }
      });
      this.optionalHosting.tags = [...this.optionalHosting.tags];
    },
    updateHostingClassificationInfo() {
      if (this.hosting.areasOfActivities.length === 0) {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Field Required'),
          color: 'error',
        });
        return;
      }

      const id = this.$route.params.id;

      const areasOfActivityIds = this.hosting.areasOfActivities.map(activity => activity.id);
      const tags = this.optionalHosting.tags.map(tag => tag);
      const ecosystemStakeholderProfiles = this.optionalHosting.ecosystem.map(
        ecosystem => ecosystem.id,
      );

      const hostingUpdateData = {
        id: id,
        areasOfActivityIds: areasOfActivityIds,
        tags: tags,
        ecosystemStakeholderProfiles: ecosystemStakeholderProfiles,
      };

      const isFormValid = this.$refs.classificationForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/hosting-institutions/classification-info', hostingUpdateData)
          .then(response => {
            console.log('Hosting Institution info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Hosting Institution Updated Successfully',
              color: 'primary200',
            });
            this.getHosting();
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating Hosting Institution info:', error);
          });
      }
    },
    cancel() {
      this.getHosting();
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 3em;
}
</style>
